import { IconProps } from '@euler/app/components/icons/types';
import { Input } from '@euler/components/form/Input';
import { AdvancedImage } from '@euler/components/image/AdvancedImage';
import { FontFamily } from '@euler/components/typography';
import { PredefinedHitSlops } from '@euler/functions/predefinedHitSlops';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { onErrorIgnore } from '@euler/utils';
import { useBehaviorSubject, usePersistFn } from '@euler/utils/hooks';
import { memo, useCallback } from 'react';
import { ActivityIndicator, Text, TouchableOpacity, View } from 'react-native';
import { Path, Svg } from 'react-native-svg';
import { useOrderFlowContext } from '../../../../OrderFlowContext';
import { useVehicleInfoLoader } from '../../functions/useVehicleInfoLoader';
import { Cell } from '../components/Cell';
import { IconWrapper } from '../components/IconWrapper';
import { Row } from '../components/Row';
import { useOrderFormStyles } from '../components/styles';

export const VehicleInfoRow = memo(
  ({
    required = true,
    ...props
  }: {
    required?: boolean;
    disabled?: boolean;
  }) => {
    const {
      theme,
      navigation,
      vehicleImageUrl$,
      formController: { form },
    } = useOrderFlowContext();

    const { rpx } = useResponsiveContext();
    const { iconColor, linkColor, placeholderColor } = theme;

    const [vehicleImageUrl, setVehicleImageUrl] =
      useBehaviorSubject(vehicleImageUrl$);

    const loadVehicleInfoByVIN = useVehicleInfoLoader();

    const onPress = usePersistFn(() => {
      if (form.vehicleInfo.loading) return;
      if (form.vehicleInfo.error) {
        form.vehicleInfo.setError(undefined);
      }
      navigation.navigate('_vehicleBrandSelector', {
        selected: {
          brand: form.vehicleInfo.value?.vehicleBrandName,
          model: form.vehicleInfo.value?.vehicleModel,
        },
        onSelect: (brand, model) => {
          form.vehicleInfo.update({
            vehicleName: `${brand} ${model.name}`,
            vehicleBrandName: brand,
            vehicleModel: model.name,
            vehicleManufacturer: model.manufacturer,
          });
          setVehicleImageUrl(model.img ?? undefined);
        },
      });
    });

    const onRetryLoadVehicleInfoByVin = useCallback(() => {
      loadVehicleInfoByVIN(form.vin.value).catch(onErrorIgnore);
    }, [form.vin.value, loadVehicleInfoByVIN]);

    const styles = useOrderFormStyles();

    return (
      <Row
        errorProp="vehicleInfo"
        icon={
          <IconWrapper>
            <RowIcon color={iconColor} />
          </IconWrapper>
        }
        detailIcon="disclosure"
        onPress={onPress}
        {...props}
      >
        <View
          css={`
            flex-direction: row;
            justify-content: space-between;
            align-items: center;
          `}
        >
          <Cell
            required={required}
            label="品牌/车型"
            css={`
              flex: 1;
            `}
          >
            <Input
              style={styles.formInput}
              placeholderTextColor={placeholderColor}
              value={form.vehicleInfo.value?.vehicleName ?? ''}
              placeholder={
                form.vehicleInfo.loading
                  ? '正在查询车型VIN信息, 请稍候...'
                  : '请输入或选择品牌车型'
              }
              errorMsg={form.vehicleInfo.error}
              editable={false}
              pointerEvents="none"
              alwaysShowError
            />
          </Cell>
          {form.vehicleInfo.loading && <ActivityIndicator />}
          {form.vehicleInfo.error && form.vin.value ? (
            <TouchableOpacity
              css={`
                border-width: 1px;
                border-color: ${linkColor};
                padding: 0 ${rpx(4)}px;
                border-radius: ${rpx(2)}px;
              `}
              hitSlop={PredefinedHitSlops.large}
              onPress={onRetryLoadVehicleInfoByVin}
              disabled={props.disabled}
            >
              <Text
                css={`
                  font-family: ${FontFamily.NotoSans.Light};
                  font-size: ${rpx(12)}px;
                  line-height: ${rpx(16)}px;
                  color: ${linkColor};
                `}
              >
                重试
              </Text>
            </TouchableOpacity>
          ) : null}
          {vehicleImageUrl &&
          !form.vehicleInfo.loading &&
          !form.vehicleInfo.error ? (
            <AdvancedImage
              uri={vehicleImageUrl}
              css={`
                width: ${rpx(67.5)}px;
                height: ${rpx(45)}px;
              `}
            />
          ) : null}
        </View>
      </Row>
    );
  },
);

const RowIcon = memo(({ size, style, color, ...props }: IconProps) => {
  const { rpx } = useResponsiveContext();
  return (
    <Svg
      width={size ?? rpx(20)}
      height={size ?? rpx(16)}
      viewBox="0 0 20 16"
      fill="none"
      style={style}
      {...props}
    >
      <Path
        d="M7.861.139c1.58-.185 2.896-.185 4.278 0 .987.185 1.974.37 2.962.616.723.185 1.25.679 1.513 1.418.395 1.172.856 2.282 1.25 3.392.066.185.33.493.461.431.922-.123 1.383.432 1.646 1.172.066.246 0 .493 0 .616l-1.12.308c.067.37.133.802.133 1.234v5.549c0 .308-.198.863-.33.925-.131.185-2.961.185-3.224.061-.724-.493-.264-1.233-.527-1.911H5.23c-.198.678-.33 1.788-.527 1.911-.526.185-3.159.185-3.29 0-.198-.123-.33-.678-.396-1.048-.065-.801 0-1.541 0-2.343 0-1.418.066-2.898.066-4.378L.03 7.846c0-.123-.066-.308 0-.493.264-.802.658-1.419 1.711-1.295.132 0 .33-.247.395-.432.33-.925.724-1.788.987-2.713C3.517 1.372 4.702.693 6.15.447 6.742.324 7.335.262 7.86.139Zm-4.41 6.165c4.476.925 8.754.925 13.097 0-.394-1.11-.723-2.096-.987-3.082-.263-.864-.79-1.295-1.645-1.357-2.106-.062-4.146-.185-6.252-.185-.659 0-1.382.123-2.04.308-.264.062-.659.247-.725.494a52.94 52.94 0 0 0-1.447 3.822Zm.856 4.686s0 .062 0 0c.33 0 .724 0 1.053-.061.263-.062.527-.247.658-.432.066-.123-.197-.493-.395-.616-.79-.37-1.645-.679-2.5-.987-.395-.123-.922-.123-1.054.493-.197.864 0 1.357.659 1.48.526.062 1.053.123 1.58.123Zm11.583 0c.461-.061 1.053-.061 1.58-.185.724-.185.592-.801.592-1.294 0-.432-.329-.802-.724-.679a25.06 25.06 0 0 0-3.027 1.11c-.46.185-.264.802.197.925.395.123.856.123 1.382.123Z"
        fill={color ?? '#207FE7'}
      />
    </Svg>
  );
});
